/**
 * Funding a SECOND resident's deposit from what the first one left (owner, 7 Oct 2026: both of a
 * couple into care, built properly).
 *
 * The first entrant's deposit is chosen asset by asset on the funding card, or by the engine's
 * funding-order candidates. The second room's deposit is not asked for the same way: a client
 * entering both of them at once has one pool of savings, and a second allocator over the same
 * assets would let the same dollar fund two rooms. So the second deposit is taken from what the
 * first plan LEFT, in the order the engine's own candidates use — bank, then term deposits, then
 * account-based pensions, then shares — never from anything held in a trust or company, and never
 * more than the asset still holds once the first deposit's claim on it is subtracted.
 */
import type { FactFind } from '../factFindTypes.ts';
import { isEntityOwnership } from '../constants/clientStructureTypes.ts';

export const SECOND_DEPOSIT_ORDER = ['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares'] as const;

/** Manual Control's funding bag: `type-id` → gross amount taken from that asset. */
export type ManualFundingKeys = Record<string, number | string>;

export interface SecondDepositAllocation {
    /** What the second deposit is funded with, in the Manual Control `type-id` keyed form. */
    radFunding: Record<string, number>;
    /** What could be raised, which may be less than asked where the first plan left too little. */
    funded: number;
    shortfall: number;
}

export function allocateSecondDepositFunding(factFind: FactFind, amount: number, alreadyClaimed: ManualFundingKeys = {}): SecondDepositAllocation {
    const radFunding: Record<string, number> = {};
    let remaining = Math.max(0, Number(amount) || 0);
    const claimed = (key: string) => Math.max(0, Number(alreadyClaimed[key]) || 0);
    for (const type of SECOND_DEPOSIT_ORDER) {
        if (remaining <= 0) break;
        const list = (factFind.assets as Record<string, Array<{ id?: string | number; value?: number | string; ownership?: unknown }> | undefined> | undefined)?.[type] || [];
        for (const asset of list) {
            if (remaining <= 0) break;
            if (asset.id == null || isEntityOwnership(String(asset.ownership ?? ''))) continue;
            const key = `${type}-${asset.id}`;
            const available = Math.max(0, (Number(asset.value) || 0) - claimed(key));
            const take = Math.min(available, remaining);
            if (take <= 0) continue;
            radFunding[key] = take;
            remaining -= take;
        }
    }
    const funded = Math.max(0, (Number(amount) || 0) - remaining);
    return { radFunding, funded, shortfall: remaining };
}
